/**
 * Rich content panels for schedule-related confirm dialogs. Each exported
 * function returns a React node meant to be passed as `message` to
 * `useConfirm`. They all share the same visual language so the user sees a
 * consistent "what am I about to do?" summary regardless of the action.
 *
 * The layout follows a three-part rhythm:
 *   1. Context header  — basket / strategy crumbs + primary identifier.
 *   2. Meta grid       — entry / LTP / P&L for single positions, or
 *                        strategy breakdown for bulk actions.
 *   3. Scroll list     — per-position (or per-strategy) rows for bulk
 *                        actions, capped at max-h-56 so the dialog never
 *                        dominates the viewport.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { CHIP_SIDE } from '../../../utils/chipColors';
import type { ScheduleRow, ScheduleStrategySummary } from '../../../types/schedule';
import { formatPnlParts } from '../../../utils/positionDisplay';

// ── primitives ────────────────────────────────────────────────────────────

function Crumbs({ basket, strategy }: { basket?: string | null; strategy?: string | null }) {
  const parts = [basket, strategy].filter(Boolean) as string[];
  if (parts.length === 0) return null;
  return (
    <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 truncate">
      {parts.join(' · ')}
    </div>
  );
}

function SideBadge({ side }: { side: string }) {
  if (!side) return null;
  // Canonical BUY=blue / SELL=orange via CHIP_SIDE — replaces previous
  // green/red text-only treatment which was both visually divergent from the
  // tables AND semantically misleading (BUY isn't always "good").
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center text-[10px] font-bold px-2 py-0.5 rounded tabular-nums',
        side === 'BUY' ? CHIP_SIDE.BUY : CHIP_SIDE.SELL,
      )}
    >
      {side}
    </span>
  );
}

function PriceRow({ label, value, highlight }: { label: string; value: React.ReactNode; highlight?: boolean }) {
  return (
    <>
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <span className={cn('tabular-nums text-right', highlight ? 'font-semibold' : 'text-gray-900 dark:text-white')}>
        {value}
      </span>
    </>
  );
}

function RejectionCallout({ message }: { message: string | null | undefined }) {
  if (!message) return null;
  return (
    <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40">
      <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
      <div className="text-xs text-red-700 dark:text-red-300 min-w-0">
        <div className="font-semibold">Rejected</div>
        <div className="break-words">{message}</div>
      </div>
    </div>
  );
}

function PositionHeader({ row }: { row: ScheduleRow }) {
  const side = row.action || row.leg.action || '';
  return (
    <div>
      <Crumbs basket={row.basket_name} strategy={row.strategy_name} />
      <div className="text-sm font-mono font-semibold text-gray-900 dark:text-white mt-0.5 break-all">
        {row.symbol || '—'}
      </div>
      <div className="flex items-center gap-2 mt-1">
        <SideBadge side={side} />
        {row.quantity > 0 && (
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 tabular-nums">× {row.quantity}</span>
        )}
        {row.product && (
          <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">· {row.product}</span>
        )}
      </div>
    </div>
  );
}

// ── public bodies ────────────────────────────────────────────────────────

/** Body for retrying a single rejected position. Surfaces the broker error. */
export function retryPositionBody(row: ScheduleRow) {
  return (
    <div className="space-y-3">
      <PositionHeader row={row} />
      <RejectionCallout message={row.entry_error_message} />
      <div className="text-[11px] text-gray-500 dark:text-gray-400">
        Submits a fresh entry order at the current market. Previous attempts are unaffected.
      </div>
    </div>
  );
}

/** Body for exiting a single open position. Shows entry / LTP / live P&L. */
export function exitPositionBody(row: ScheduleRow) {
  const pnl = row.pnl != null ? formatPnlParts(row.pnl) : null;
  // Prefer the broker's filled (average) entry price — the actual cost
  // basis the user is exiting against — and fall back to the placed limit
  // only when the order hasn't filled yet (e.g. ENTRY_PENDING cancel).
  const entryShown = row.average_entry_price ?? row.entry_price;
  const hasPrices = entryShown != null || row.ltp != null || pnl != null;
  const isPending = row.position_status === 'ENTRY_PENDING' && row.entry_status !== 'REJECTED';

  return (
    <div className="space-y-3">
      <PositionHeader row={row} />
      {hasPrices && (
        <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-xs">
          {entryShown != null && (
            <PriceRow label="Entry" value={`₹${entryShown.toFixed(2)}`} />
          )}
          {row.ltp != null && (
            <PriceRow label="LTP" value={`₹${row.ltp.toFixed(2)}`} />
          )}
          {pnl && (
            <PriceRow label="P&L" value={<span className={pnl.className}>{pnl.text}</span>} highlight />
          )}
        </div>
      )}
      <div className="text-[11px] text-gray-500 dark:text-gray-400">
        {isPending
          ? 'Cancels the pending entry order at the broker.'
          : 'Places an exit order immediately at the best available price.'}
      </div>
    </div>
  );
}

/** Body listing every position that will be retried or exited in a strategy. */
export function strategyBulkBody({
  strategyName,
  basketName,
  positions,
  action,
}: {
  strategyName: string | null | undefined;
  basketName: string | null | undefined;
  positions: ScheduleRow[];
  action: 'retry' | 'exit';
}) {
  const count = positions.length;
  const totalPnl = positions.reduce((sum, r) => sum + (r.pnl ?? 0), 0);
  const pnlFmt = formatPnlParts(totalPnl);

  return (
    <div className="space-y-3">
      <div>
        <Crumbs basket={basketName} strategy={strategyName} />
        <div className="text-sm text-gray-800 dark:text-gray-100 mt-1">
          {action === 'retry'
            ? <>Retrying <span className="font-semibold tabular-nums">{count}</span> rejected {count === 1 ? 'entry' : 'entries'}</>
            : <>Exiting <span className="font-semibold tabular-nums">{count}</span> open {count === 1 ? 'position' : 'positions'}</>}
        </div>
        {action === 'exit' && count > 0 && (
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 tabular-nums">
            Net unrealized <span className={cn('font-semibold', pnlFmt.className)}>{pnlFmt.text}</span>
          </div>
        )}
      </div>

      <div className="max-h-56 overflow-auto rounded-md border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/60">
        {positions.map((r) => {
          const side = r.action || r.leg.action || '';
          const pnl = r.pnl != null ? formatPnlParts(r.pnl) : null;
          return (
            <div key={r.row_key || r.position_id || r.symbol} className="px-3 py-2 flex items-start gap-2 text-xs">
              <div className="min-w-0 flex-1">
                <div className="font-mono truncate text-gray-700 dark:text-gray-200">{r.symbol || '—'}</div>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <SideBadge side={side} />
                  {r.quantity > 0 && (
                    <span className="text-gray-600 dark:text-gray-300 tabular-nums">× {r.quantity}</span>
                  )}
                  {action === 'retry' && r.entry_error_message && (
                    <span className="text-[11px] text-red-500 dark:text-red-400 italic truncate" title={r.entry_error_message}>
                      · {r.entry_error_message}
                    </span>
                  )}
                </div>
              </div>
              {action === 'exit' && pnl && (
                <div className={cn('tabular-nums font-semibold shrink-0', pnl.className)}>{pnl.text}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Basket-level aggregate — per-strategy breakdown of open / rejected counts. */
export function basketBulkBody({
  basketName,
  strategies,
  action,
}: {
  basketName: string | null | undefined;
  strategies: ScheduleStrategySummary[];
  action: 'retry' | 'exit';
}) {
  const relevant = strategies.filter((s) =>
    action === 'exit' ? s.open_count > 0 : s.entry_counts.rejected > 0,
  );
  const totalCount = relevant.reduce(
    (n, s) => n + (action === 'exit' ? s.open_count : s.entry_counts.rejected),
    0,
  );
  const totalPnl = relevant.reduce((n, s) => n + s.total_pnl, 0);
  const pnlFmt = formatPnlParts(totalPnl);

  return (
    <div className="space-y-3">
      <div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
          Basket
        </div>
        <div className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5 truncate">
          {basketName || '—'}
        </div>
        <div className="text-sm text-gray-800 dark:text-gray-100 mt-1">
          {action === 'retry'
            ? <>Retrying <span className="font-semibold tabular-nums">{totalCount}</span> rejected {totalCount === 1 ? 'entry' : 'entries'} across <span className="font-semibold tabular-nums">{relevant.length}</span> {relevant.length === 1 ? 'strategy' : 'strategies'}</>
            : <>Exiting <span className="font-semibold tabular-nums">{totalCount}</span> open {totalCount === 1 ? 'position' : 'positions'} across <span className="font-semibold tabular-nums">{relevant.length}</span> {relevant.length === 1 ? 'strategy' : 'strategies'}</>}
        </div>
        {action === 'exit' && totalCount > 0 && (
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 tabular-nums">
            Net unrealized <span className={cn('font-semibold', pnlFmt.className)}>{pnlFmt.text}</span>
          </div>
        )}
      </div>

      <div className="max-h-56 overflow-auto rounded-md border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/60">
        {relevant.map((s) => {
          const pnl = formatPnlParts(s.total_pnl);
          const count = action === 'exit' ? s.open_count : s.entry_counts.rejected;
          return (
            <div key={`${s.basket_id}#${s.strategy_id}`} className="px-3 py-2 flex items-center gap-2 text-xs">
              <div className="min-w-0 flex-1">
                <div className="text-gray-800 dark:text-gray-100 font-medium truncate">
                  {s.strategy_name || '—'}
                </div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                  {count} {action === 'exit' ? (count === 1 ? 'open position' : 'open positions') : (count === 1 ? 'rejected entry' : 'rejected entries')}
                </div>
              </div>
              {action === 'exit' && (
                <div className={cn('tabular-nums font-semibold shrink-0', pnl.className)}>{pnl.text}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
